import {GlobalStyles, ScopedCssBaseline, ThemeProvider} from "@mui/material";
import {alpha, SxProps, Theme} from "@mui/material/styles";
import React, {useMemo} from "react";
import {signingControls} from "./signingColors";
import {useFont} from "../../common/newFontPicker/fonts";
import {signingThemeToMuiTheme} from "./signingTheme";
import {SnapshotTheme} from "./digitalSigning.types";

interface Props {
    theme?: SnapshotTheme
    /** The guest's page: the paper fills the screen and takes over the document around it. */
    page?: boolean
    /** On the page's paper, e.g. a white background for printing. */
    sx?: SxProps<Theme>
    children: React.ReactNode
}

const paperSx = (muiTheme: Theme) => {
    const controls = signingControls(muiTheme);

    return {
        bgcolor: 'background.default',
        color: 'text.primary',
        colorScheme: 'light',
        caretColor: controls.accent,
        WebkitTapHighlightColor: 'transparent',
        '& ::selection': {backgroundColor: alpha(controls.fill, 0.24)},
        '& a': {color: controls.accent, textUnderlineOffset: '3px'},
        '& button:focus-visible': {outline: `2px solid ${controls.focus}`, outlineOffset: 3},
    };
};

/** Theme, font, and paper container for signing views. */
function SigningFrame({theme, page, sx, children}: Props) {
    const muiTheme = useMemo(() => signingThemeToMuiTheme(theme), [theme]);
    useFont(theme?.fontSource, theme?.fontUrl, theme?.fontFamily);

    return (
        <ThemeProvider theme={muiTheme}>
            {page && (
                // No pull-to-refresh or rubber band: an accidental reload would drop the guest's tip and signature
                <GlobalStyles
                    styles={{
                        'html, body': {overscrollBehavior: 'none', backgroundColor: muiTheme.palette.background.default},
                        html: {scrollbarGutter: 'stable'},
                    }}
                />
            )}
            <ScopedCssBaseline
                sx={[
                    paperSx(muiTheme),
                    !!page && {minHeight: '100vh', '@supports (min-height: 100dvh)': {minHeight: '100dvh'}, overscrollBehavior: 'none'},
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
            >
                {children}
            </ScopedCssBaseline>
        </ThemeProvider>
    );
}

export default SigningFrame;
